import { supabase } from '@/foundation/altzero_base/base/utils/supabaseClient';
import type {
  BvDistributionTariffUsage, BvRetailTouUsage,
  BvDistributionTreatmentInterval, BucketRollup, BvTariffBucketConfig,
} from '@/features/billValidation/types/billValidationTypes';
import type { ApiResponse } from '@/features/billValidation/types/billValidationTypes';
import { monthEnd } from '@/features/billValidation/lib/billMonths';
import type { IntervalGapRow } from '@/features/billValidation/lib/usageCoverage';

export const usageService = {
  /**
   * Every bucket CONFIGURED on the site's network tariff, whether or not it
   * produced usage rows. The stepper joins results onto this so an empty bucket
   * shows with a reason instead of vanishing — a table built from results alone
   * made EA256 look like a 2-bucket tariff when it has 4.
   */
  getTariffConfig: async (
    siteId: string,
  ): Promise<ApiResponse<BvTariffBucketConfig[]>> => {
    try {
      const { data, error } = await supabase.rpc('bv_site_tariff_config', {
        p_site_id: siteId,
      });
      if (error) throw error;
      return { success: true, data: (data ?? []) as BvTariffBucketConfig[] };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /** Day rows for the period. Treatment buckets are NOT here — see getTreatmentPeak. */
  getDistributionBuckets: async (
    siteId: string, from: string, to: string,
  ): Promise<ApiResponse<BvDistributionTariffUsage[]>> => {
    try {
      const { data, error } = await supabase
        .from('bv_distribution_tariff_usage')
        .select('*')
        .eq('site_id', siteId)
        .gte('usage_date', from)
        .lte('usage_date', to)
        .order('usage_date', { ascending: true });
      if (error) throw error;
      return { success: true, data: data ?? [] };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  getRetailBands: async (
    siteId: string, from: string, to: string,
  ): Promise<ApiResponse<BvRetailTouUsage[]>> => {
    try {
      const { data, error } = await supabase
        .from('bv_retail_tou_usage')
        .select('*')
        .eq('site_id', siteId)
        .gte('usage_date', from)
        .lte('usage_date', to)
        .order('usage_date', { ascending: true });
      if (error) throw error;
      return { success: true, data: data ?? [] };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /**
   * The period figure for a treatment bucket. Calls bv_reduce_bucket rather
   * than reducing here: a second implementation in TypeScript is exactly the
   * drift the spec's parity concern (§6.7) is about.
   */
  getTreatmentPeak: async (
    siteId: string, bucketKey: string, from: string, to: string,
    kind: 'interval' | 'accumulate', params: Record<string, unknown> = {},
  ): Promise<ApiResponse<BucketRollup>> => {
    try {
      const { data, error } = await supabase.rpc('bv_reduce_bucket', {
        p_site_id: siteId, p_bucket_key: bucketKey,
        p_from: from, p_to: to, p_kind: kind, p_params: params,
      });
      if (error) throw error;
      return { success: true, data: { bucket_key: bucketKey, stale: false, ...data } as BucketRollup };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /**
   * Dates in the range that hold interval data for the site (any suffix or
   * provider), for the Tariff Usage Report by NMI. Paged: a site with two suffixes has
   * ~730 rows a year, past PostgREST's 1000-row cap over longer ranges.
   */
  getCoveredDates: async (
    siteId: string, from: string, to: string,
  ): Promise<ApiResponse<Set<string>>> => {
    try {
      const PAGE = 1000;
      const dates = new Set<string>();
      for (let start = 0; ; start += PAGE) {
        const { data, error } = await supabase
          .from('bv_nem_interval')
          .select('interval_date')
          .eq('site_id', siteId)
          .gte('interval_date', from)
          .lte('interval_date', to)
          .order('interval_date', { ascending: true })
          .order('nmi_suffix', { ascending: true })
          .order('provider', { ascending: true })
          .range(start, start + PAGE - 1);
        if (error) throw error;
        for (const row of data ?? []) dates.add(row.interval_date as string);
        if (!data || data.length < PAGE) return { success: true, data: dates };
      }
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /**
   * Tariff Usage Report, Bulk tab: for the given site batches (gapBatches), the
   * days in `fromMonth`..`toMonth` with no interval data (RPC bv_interval_gaps,
   * gaps only, capped at yesterday). Batches run a few at a time; `onProgress`
   * gets the number done. Each batch returns under PostgREST's 1,000-row cap.
   */
  getIntervalGaps: async (
    fromMonth: string, toMonth: string, batches: string[][], onProgress?: (done: number) => void,
  ): Promise<ApiResponse<IntervalGapRow[]>> => {
    try {
      const CONCURRENCY = 4;
      const rows: IntervalGapRow[] = [];
      let next = 0;
      let done = 0;
      const worker = async () => {
        while (next < batches.length) {
          const ids = batches[next++];
          const { data, error } = await supabase
            .rpc('bv_interval_gaps', { p_from: `${fromMonth}-01`, p_to: monthEnd(toMonth), p_site_ids: ids });
          if (error) throw error;
          rows.push(...((data ?? []) as IntervalGapRow[]));
          onProgress?.(++done);
        }
      };
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, batches.length) }, worker));
      return { success: true, data: rows };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /** The drill-down: which intervals set this charge. */
  getTreatmentIntervals: async (
    siteId: string, bucketKey: string, from: string, to: string, limit = 20,
  ): Promise<ApiResponse<BvDistributionTreatmentInterval[]>> => {
    try {
      const { data, error } = await supabase
        .from('bv_distribution_treatment_interval')
        .select('*')
        .eq('site_id', siteId)
        .eq('bucket_key', bucketKey)
        .gte('usage_date', from)
        .lte('usage_date', to)
        .order('power_chargeable', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return { success: true, data: data ?? [] };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },
};